import { Loader } from "@marketplace/ui";
import { forwardRef, useEffect, useState } from "react";

import { httpClient } from "@/http";

interface ProtectedImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  alt: string;
}

export const ProtectedImage = forwardRef<HTMLImageElement, ProtectedImageProps>(
  ({ src, alt, ...props }, ref) => {
    const [imageUrl, setImageUrl] = useState<string>("");
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<boolean>(false);

    useEffect(() => {
      let isMounted = true;
      setIsLoading(true);
      setError(false);
      setImageUrl("");

      const fetchImage = async () => {
        try {
          const response = await httpClient.get(src);

          if (!response.ok) throw new Error("Image fetch failed");

          const blob = await response.blob();
          const url = URL.createObjectURL(blob);

          if (isMounted) {
            setImageUrl(url);
            setIsLoading(false);
          }
        } catch {
          if (isMounted) {
            setError(true);
            setIsLoading(false);
          }
        }
      };

      fetchImage();
      return () => {
        isMounted = false;
        if (imageUrl) URL.revokeObjectURL(imageUrl);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [src]);

    if (isLoading) {
      return (
        <div className="w-full h-full flex justify-center items-center">
          <Loader color="gray" />
        </div>
      );
    }

    if (error) {
      return (
        <img ref={ref} src="/images/broken-image.svg" alt={alt} {...props} />
      );
    }

    return <img ref={ref} src={imageUrl} alt={alt} {...props} />;
  }
);

ProtectedImage.displayName = "Image";

interface ProtectedPdfViewerProps extends React.IframeHTMLAttributes<HTMLIFrameElement> {
  src: string;
}

export const ProtectedPdfViewer = forwardRef<
  HTMLIFrameElement,
  ProtectedPdfViewerProps
>(({ src, className, ...props }, ref) => {
  const [pdfUrl, setPdfUrl] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    setError(false);
    setPdfUrl("");

    const fetchPdf = async () => {
      try {
        const response = await httpClient.get(src);

        if (!response.ok) throw new Error("PDF fetch failed");

        const blob = await response.blob();
        const url = URL.createObjectURL(blob);

        if (isMounted) {
          setPdfUrl(url);
          setIsLoading(false);
        }
      } catch {
        if (isMounted) {
          setError(true);
          setIsLoading(false);
        }
      }
    };

    fetchPdf();
    return () => {
      isMounted = false;
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);

  if (isLoading) {
    return (
      <div className="w-full h-full flex justify-center items-center">
        <Loader color="gray" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full h-full flex justify-center items-center text-gray-500">
        Failed to load PDF
      </div>
    );
  }

  return (
    <iframe
      ref={ref}
      src={pdfUrl}
      className={className}
      title="PDF Document"
      {...props}
    />
  );
});

ProtectedPdfViewer.displayName = "ProtectedPdfViewer";
